import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Typography, Button, Grid } from "@material-ui/core";
import { useServiceFeeFormStyles } from "./styles";
import ColoredLine from "../../ColoredLine";
import ServiceFee from "./ServiceFee";
import useHasPermissions from "../../../hooks/useHasPermissions";
import { useConfirmationDialog } from "../../../hooks/useConfirmationDialog";
import serviceFeesService from "../../../services/ServiceFeesService";
import apiClient from "../../../auth/apiClient";
import { useTheme } from "@material-ui/core/styles";
import { isNull, isUndefined } from "lodash";
import RateService from "../../../services/RateService";

const serviceFeeService = new serviceFeesService(apiClient);

const ServiceFeeForm = ({ entityID }) => {
  const classes = useServiceFeeFormStyles();
  const { textConfirmation } = useConfirmationDialog();
  const { hasPermissions } = useHasPermissions();
  const addServiceFeesPermission = hasPermissions(["rates.add"]);
  const [listServiceFees, setListServiceFees] = useState([]);
  const theme = useTheme();

  const defaultValues = {
    entityID: entityID,
    name: "",
    amount: "",
    applyZeroTransactionFlag: false,
    rateNames: []
  };

  const fetchServiceFees = useCallback(async entityID => {
    try {
      const response = await serviceFeeService.getPageableServiceFees(entityID);
      return response.data.collection;
    } catch {
      return [];
    }
  }, [entityID]);

  useEffect(() => {
    fetchServiceFees(entityID).then(info => {
      if (!isUndefined(info)) {
        const serviceFeeWithAmountNumeric = info.map(item => {
          item.amount = formatCurrency(item.amount);
          return item;
        });
        setListServiceFees(serviceFeeWithAmountNumeric);
      }
    });
  }, [entityID]);

  const onServiceFeeSubmit = async (values, index) => {
    let serviceFeeValues = values;
    if (isUndefined(values) || isNull(values)) {
      return;
    }
    serviceFeeValues.amount = formatCurrency(serviceFeeValues.amount);

    if (serviceFeeValues.serviceFeeID === 0 || isUndefined(serviceFeeValues.serviceFeeID)) {
      try {
        const response = await serviceFeeService.createServiceFee(entityID, serviceFeeValues);
        if (response.status === 200) {
          response.data.amount = formatCurrency(response.data.amount);
          replaceServiceFeeItemInStateList(response.data, index);
        }
      } catch {
        console.log("Failed to create service fee");
      }
    } else {
      try {
        const response = await serviceFeeService.updateServiceFee(entityID, serviceFeeValues);
        if (response.status === 200) {
          response.data.amount = formatCurrency(response.data.amount);
          replaceServiceFeeItemInStateList(response.data, index);
        }
      } catch {
        console.log("Failed to update service fee");
      }
    }
  };

  const handleDeleteServiceFee = async (entityID, serviceFeeID, currentIndex) => {
    if (serviceFeeID === 0) {
      setListServiceFees(listServiceFees.filter((item, index) => currentIndex !== index));
      return;
    }

    const deleteServiceFee = listServiceFees.find(serviceFee => serviceFeeID === serviceFee.serviceFeeID);
    if (!deleteServiceFee) return;

    const confirmed = await textConfirmation({
      title: "Delete Service Fee Setting",
      message: "Are you sure you want to delete this service fee?",
      textConfirmation: listServiceFees[currentIndex].name.toUpperCase(),
      buttons: [
        { name: "Yes", color: "secondary" },
        { name: "No", color: theme.palette.text.disabled }
      ]
    });

    if (confirmed) {
      try {
        const response = await serviceFeeService.deleteServiceFee(entityID, serviceFeeID);
        if (response.status === 200) {
          setListServiceFees(prevListServiceFees =>
            prevListServiceFees.filter(item => serviceFeeID !== item.serviceFeeID)
          );
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleAddServiceFee = () => {
    setListServiceFees([...listServiceFees, defaultValues]);
  };

  const replaceServiceFeeItemInStateList = (newData, currentIndex) => {
    setListServiceFees(
      listServiceFees.map((serviceFee, index) => (index === currentIndex ? newData : serviceFee))
    );
  };

  const formatCurrency = val => {
    let newValue = val;
    if (!isNaN(parseFloat(val))) {
      const nonPaddedVal = `${val}`;
      if (nonPaddedVal.includes(".")) {
        const [, afterDecimal] = nonPaddedVal.split(".");
        if (afterDecimal.length === 1) newValue = nonPaddedVal + "0";
        else if (afterDecimal.length === 0) newValue = nonPaddedVal + "00";
      } else {
        newValue = nonPaddedVal + ".00";
      }
    }
    return newValue;
  };

  return (
    <>
      <Grid container className={classes.serviceFeeHeader}>
        <Typography variant="h5" color="primary" className={classes.title}>
          Service Fees
        </Typography>
        <Button
          className={classes.addServiceFeeBtn}
          variant="outlined"
          color="primary"
          data-testid="add-btn"
          onClick={handleAddServiceFee}
          disabled={!addServiceFeesPermission}
        >
          Add
        </Button>
      </Grid>
      <ColoredLine className={classes.headerBreak} />
      {listServiceFees.map((serviceFee, index) => (
        <ServiceFee
          key={`service-fee-${index}`}
          index={index}
          {...serviceFee}
          entityID={entityID}
          onServiceFeeSubmit={onServiceFeeSubmit}
          onDelete={() => handleDeleteServiceFee(entityID, serviceFee.serviceFeeID, index)}
          formatCurrency={formatCurrency}
        />
      ))}
    </>
  );
};

ServiceFeeForm.propTypes = {
  entityID: PropTypes.string.isRequired
};

export default ServiceFeeForm;
